import {
  ConfirmModal,
  CustomPageContainer,
  FilterSearchBar,
  Table,
  TablePaginationContext,
} from '@/components';
import { TableRow, TableSort } from '@/components/Table/types';
import { deleteRowColumnConfig } from '@/utils';
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useToggleProcessStatus } from '../../hooks/useToggleProcessStatus';
import { useDeleteProcess, useGetProcesses } from '../../queries';
import { getProcessesColumns, getProcessesColumnsNamedMapped } from './utils';

export const ProcessesPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tableSort, setTableSort] = useState<TableSort[]>([]);
  const { onChange: onProcessStatusChange } = useToggleProcessStatus();
  const [processSelected, setProcessSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const getAllProcessesOptions = useMemo(
    () => ({
      onError: () => {
        toast.error(t('error_getting_processes'));
      },
    }),
    [t],
  );

  const { searchTerm, page, onPageChange, handleSearchChange } = useContext(
    TablePaginationContext,
  );

  const handleOpenRemoveProcessModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setProcessSelected(id);
  }, []);

  const handleCloseRemoveProcessModalState = useCallback(() => {
    setIsOpenDelete(false);
    setProcessSelected(undefined);
  }, []);

  const { data: processes, isFetching: isLoadingProcesses } = useGetProcesses(
    page,
    searchTerm,
    getProcessesColumnsNamedMapped(tableSort?.[0]?.columnId ?? 'name'),
    tableSort?.[0]?.direction ?? 'ascending',
    getAllProcessesOptions,
  );

  const { mutate: deleteProcess, isLoading: isLoadingDelete } =
    useDeleteProcess();

  const onSort = (data: TableSort[]) => {
    setTableSort(data);
  };

  const routeChange = useCallback(
    (row?: TableRow) => {
      const path = `${row?.id}`;
      navigate(path);
    },
    [navigate],
  );

  const handleOnStatusChange = useCallback(
    (checked: boolean, row: TableRow) => {
      onProcessStatusChange(checked, +row.id);
    },
    [onProcessStatusChange],
  );

  const handleDeleteProcess = useCallback(() => {
    if (!processSelected) {
      return;
    }

    deleteProcess(processSelected);

    handleCloseRemoveProcessModalState();
  }, [processSelected, deleteProcess, handleCloseRemoveProcessModalState]);

  return (
    <CustomPageContainer>
      <CustomPageContainer.Header title={t('processes')} />
      <FilterSearchBar
        total={processes?.totalRows || 0}
        title={t('processes')}
        onSearch={handleSearchChange}
        variant="secondary"
      />
      <Table
        columns={[
          ...getProcessesColumns({
            onClick: routeChange,
            onStatusChange: handleOnStatusChange,
          }),
          deleteRowColumnConfig({
            disabledMessage: t('delete_process'),
            onDeleteClick(row) {
              handleOpenRemoveProcessModalState(row?.id as number);
            },
          }),
        ]}
        rows={processes?.items ?? []}
        isLoading={isLoadingProcesses || isLoadingDelete}
        noResultsLabel={t('no_processes_available')}
        withPagination
        totalItems={processes?.totalRows}
        totalPages={processes?.totalPages}
        currentPage={page}
        onPageChange={onPageChange}
        onSort={onSort}
      />
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveProcessModalState}
        title={t('please_confirm_action')}
        message={t('confirm_process_delete')}
        onConfirm={handleDeleteProcess}
      />
    </CustomPageContainer>
  );
};
